<template>
  <div class="system-config-container">
    <!-- 统一控制中心 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-setting"></i>
          <span>{{ $t('system.systemConfigTips') }}</span>
        </div>
      </div>
    </div>
    
    <!-- Configuration Tabs -->
    <el-tabs v-model="activeTab" class="config-tabs" v-loading="loading">
      <el-tab-pane name="basic">
        <span slot="label"><i class="el-icon-info"></i> {{ $t('system.basicSettings') }}</span>
        <div class="config-content">
          <div class="section-header">
            <h3>{{ $t('system.basicSettings') }}</h3>
            <p class="section-desc">{{ $t('system.basicSettingsDesc') }}</p>
          </div>
          <el-card class="config-card" shadow="never">
            <el-form :model="basicConfig" label-width="150px" :rules="basicConfigRules" ref="basicConfigForm">
              <el-form-item :label="$t('system.systemName')" prop="systemName">
                <el-input v-model="basicConfig.systemName" :placeholder="$t('system.enterSystemName')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.systemVersion')" prop="systemVersion">
                <el-input v-model="basicConfig.systemVersion" disabled></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.companyName')" prop="companyName">
                <el-input v-model="basicConfig.companyName" :placeholder="$t('system.enterCompanyName')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.contactEmail')" prop="contactEmail">
                <el-input v-model="basicConfig.contactEmail" type="email" :placeholder="$t('system.enterContactEmail')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.defaultLanguage')" prop="defaultLanguage">
                <el-select v-model="basicConfig.defaultLanguage" :placeholder="$t('common.selectPlaceholder')" style="width: 100%">
                  <el-option :label="$t('system.chinese')" value="zh-CN"></el-option>
                  <el-option :label="$t('system.english')" value="en-US"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item :label="$t('system.timeZone')" prop="timeZone">
                <el-select v-model="basicConfig.timeZone" :placeholder="$t('common.selectPlaceholder')" style="width: 100%">
                  <el-option :label="$t('system.gmt8ChinaTime')" value="Asia/Shanghai"></el-option>
                  <el-option :label="$t('system.gmt5UsEastern')" value="America/New_York"></el-option>
                  <el-option :label="$t('system.gmt0Greenwich')" value="UTC"></el-option>
                  <el-option :label="$t('system.gmt9Japan')" value="Asia/Tokyo"></el-option>
                </el-select>
              </el-form-item>
              <div class="form-actions">
                <el-button type="primary" @click="handleSaveBasicConfig" v-if="hasPermission('sys:config:update')" icon="el-icon-check">{{ $t('common.save') }}</el-button>
                <el-button @click="handleResetBasicConfig" icon="el-icon-refresh-left">{{ $t('common.reset') }}</el-button>
              </div>
            </el-form>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane name="server">
        <span slot="label"><i class="el-icon-monitor"></i> {{ $t('system.serverSettings') }}</span>
        <div class="config-content">
          <div class="section-header">
            <h3>{{ $t('system.serverSettings') }}</h3>
            <p class="section-desc">{{ $t('system.serverSettingsDesc') }}</p>
          </div>
          <el-card class="config-card" shadow="never">
            <el-form :model="serverConfig" label-width="150px" :rules="serverConfigRules" ref="serverConfigForm">
              <el-form-item :label="$t('system.serverWebsocket')" prop="serverWebsocket">
                <el-input 
                  type="textarea" 
                  :rows="3" 
                  v-model="serverConfig.serverWebsocket" 
                  :placeholder="$t('system.enterServerWebsocket')"
                ></el-input>
                <div class="form-tip">{{ $t('system.enterServerWebsocketTip') }}</div>
              </el-form-item>
              <el-form-item :label="$t('system.serverSecret')" prop="serverSecret">
                <el-input v-model="serverConfig.serverSecret" :placeholder="$t('system.enterServerSecret')">
                  <el-button slot="append" icon="el-icon-refresh" @click="handleInitSecret" v-if="hasPermission('sys:config:update')">
                    {{ $t('system.generateSecret') }}
                  </el-button>
                </el-input>
              </el-form-item>
              <el-form-item :label="$t('system.serverMqttGateway')" prop="serverMqttGateway">
                <el-input v-model="serverConfig.serverMqttGateway" :placeholder="$t('system.enterServerMqttGateway')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.serverOta')" prop="serverOta">
                <el-input v-model="serverConfig.serverOta" :placeholder="$t('system.enterServerOta')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.allowUserRegister')" prop="allowUserRegister">
                <el-switch v-model="serverConfig.allowUserRegister" active-color="#13ce66"></el-switch>
              </el-form-item>
              <div class="form-actions">
                <el-button type="primary" @click="handleSaveServerConfig" v-if="hasPermission('sys:config:update')" icon="el-icon-check">{{ $t('common.save') }}</el-button>
                <el-button @click="handleResetServerConfig" icon="el-icon-refresh-left">{{ $t('common.reset') }}</el-button>
              </div>
            </el-form>
          </el-card>
        </div>
      </el-tab-pane>
      
      <el-tab-pane name="security">
        <span slot="label"><i class="el-icon-lock"></i> {{ $t('system.securitySettings') }}</span>
        <div class="config-content">
          <div class="section-header">
            <h3>{{ $t('system.securitySettings') }}</h3>
            <p class="section-desc">{{ $t('system.securitySettingsDesc') }}</p>
          </div>
          <el-card class="config-card" shadow="never">
            <el-form :model="securityConfig" label-width="150px" :rules="securityConfigRules" ref="securityConfigForm">
              <el-form-item :label="$t('system.passwordPolicy')" prop="passwordPolicy">
                <el-select v-model="securityConfig.passwordPolicy" :placeholder="$t('common.selectPlaceholder')" style="width: 100%">
                  <el-option :label="$t('system.simple')" value="simple"></el-option>
                  <el-option :label="$t('system.medium')" value="medium"></el-option>
                  <el-option :label="$t('system.strong')" value="strong"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item :label="$t('system.sessionTimeout')" prop="sessionTimeout">
                <el-input-number v-model="securityConfig.sessionTimeout" :min="30" :max="480" style="width: 200px;"></el-input-number>
                <span style="margin-left: 10px; color: #64748b;">{{ $t('system.minutes') }}</span>
              </el-form-item>
              <el-form-item :label="$t('system.maxLoginAttempts')" prop="maxLoginAttempts">
                <el-input-number v-model="securityConfig.maxLoginAttempts" :min="3" :max="10" style="width: 200px;"></el-input-number>
                <span style="margin-left: 10px; color: #64748b;">{{ $t('system.times') }}</span>
              </el-form-item>
              <div class="form-actions">
                <el-button type="primary" @click="handleSaveSecurityConfig" v-if="hasPermission('sys:config:update')" icon="el-icon-check">{{ $t('common.save') }}</el-button>
                <el-button @click="handleResetSecurityConfig" icon="el-icon-refresh-left">{{ $t('common.reset') }}</el-button>
              </div>
            </el-form>
          </el-card>
        </div>
      </el-tab-pane>
      
      <el-tab-pane name="email">
        <span slot="label"><i class="el-icon-message"></i> {{ $t('system.emailSettings') }}</span>
        <div class="config-content">
          <div class="section-header">
            <h3>{{ $t('system.emailSettings') }}</h3>
            <p class="section-desc">{{ $t('system.emailSettingsDesc') }}</p>
          </div>
          <el-card class="config-card" shadow="never">
            <el-form :model="emailConfig" label-width="150px" :rules="emailConfigRules" ref="emailConfigForm">
              <el-form-item :label="$t('system.smtpServer')" prop="smtpServer">
                <el-input v-model="emailConfig.smtpServer" :placeholder="$t('system.enterSmtpServer')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.smtpPort')" prop="smtpPort">
                <el-input-number v-model="emailConfig.smtpPort" :min="1" :max="65535" style="width: 200px;"></el-input-number>
              </el-form-item>
              <el-form-item :label="$t('system.senderEmail')" prop="senderEmail">
                <el-input v-model="emailConfig.senderEmail" type="email" :placeholder="$t('system.enterSenderEmail')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.senderPassword')" prop="senderPassword">
                <el-input v-model="emailConfig.senderPassword" type="password" :placeholder="$t('system.enterSenderPassword')" show-password></el-input>
              </el-form-item>
              <div class="form-actions">
                <el-button type="primary" @click="handleSaveEmailConfig" v-if="hasPermission('sys:config:update')" icon="el-icon-check">{{ $t('common.save') }}</el-button>
                <el-button @click="handleResetEmailConfig" icon="el-icon-refresh-left">{{ $t('common.reset') }}</el-button>
                <el-button type="success" @click="handleTestEmail" v-if="hasPermission('sys:config:update')" icon="el-icon-position">{{ $t('system.testEmail') }}</el-button>
              </div>
            </el-form>
          </el-card>
        </div>
      </el-tab-pane>
      
      <el-tab-pane name="storage">
        <span slot="label"><i class="el-icon-receiving"></i> {{ $t('system.storageSettings') }}</span>
        <div class="config-content">
          <div class="section-header">
            <h3>{{ $t('system.storageSettings') }}</h3>
            <p class="section-desc">{{ $t('system.storageSettingsDesc') }}</p>
          </div>
          <el-card class="config-card" shadow="never">
            <el-form :model="storageConfig" label-width="150px" :rules="storageConfigRules" ref="storageConfigForm">
              <el-form-item :label="$t('system.storagePath')" prop="storagePath">
                <el-input v-model="storageConfig.storagePath" :placeholder="$t('system.enterStoragePath')"></el-input>
              </el-form-item>
              <el-form-item :label="$t('system.maxFileSize')" prop="maxFileSize">
                <el-input-number v-model="storageConfig.maxFileSize" :min="1" :max="100" style="width: 200px;"></el-input-number>
                <span style="margin-left: 10px; color: #64748b;">{{ $t('system.megabytes') }}</span>
              </el-form-item>
              <el-form-item :label="$t('system.cleanupDays')" prop="cleanupDays">
                <el-input-number v-model="storageConfig.cleanupDays" :min="7" :max="365" style="width: 200px;"></el-input-number>
                <span style="margin-left: 10px; color: #64748b;">{{ $t('system.days') }}</span>
              </el-form-item>
              <div class="form-actions">
                <el-button type="primary" @click="handleSaveStorageConfig" v-if="hasPermission('sys:config:update')" icon="el-icon-check">{{ $t('common.save') }}</el-button>
                <el-button @click="handleResetStorageConfig" icon="el-icon-refresh-left">{{ $t('common.reset') }}</el-button>
                <el-button type="warning" @click="handleCleanupStorage" v-if="hasPermission('sys:config:update')" icon="el-icon-delete">{{ $t('system.cleanupStorage') }}</el-button>
              </div>
            </el-form>
          </el-card>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { 
  getSystemConfig, 
  updateBasicConfig,
  updateSecurityConfig,
  updateEmailConfig,
  updateStorageConfig,
  updateServerConfig,
  getServerConfig,
  initServerSecret,
  testEmailConfig, 
  cleanupStorage 
} from '@/apis/system/system'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'SystemConfig',
  data() {
    return {
      activeTab: 'basic',
      loading: false,
      
      // Basic configuration
      basicConfig: {
        systemName: 'ESP32 Manager',
        systemVersion: '1.0.0',
        companyName: '',
        contactEmail: '',
        defaultLanguage: 'zh-CN',
        timeZone: 'Asia/Shanghai'
      },

      // Server configuration
      serverConfig: {
        serverWebsocket: '',
        serverSecret: '',
        serverMqttGateway: '',
        serverOta: '',
        allowUserRegister: true
      },
      
      // Security configuration
      securityConfig: {
        passwordPolicy: 'medium',
        sessionTimeout: 120,
        maxLoginAttempts: 5
      },
      
      // Email configuration
      emailConfig: {
        smtpServer: '',
        smtpPort: 587,
        senderEmail: '',
        senderPassword: ''
      },
      
      // Storage configuration
      storageConfig: {
        storagePath: './storage',
        maxFileSize: 10,
        cleanupDays: 30
      }
    }
  },
  
  computed: {
    // Validation rules
    basicConfigRules() {
      return {
        systemName: [
          { required: true, message: this.$t('system.enterSystemName'), trigger: 'blur' }
        ],
        contactEmail: [
          { type: 'email', message: this.$t('system.enterValidEmail'), trigger: 'blur' }
        ]
      }
    },

    serverConfigRules() {
      return {
        serverWebsocket: [
          { required: true, message: this.$t('system.enterServerWebsocket'), trigger: 'blur' }
        ],
        serverSecret: [
          { required: true, message: this.$t('system.enterServerSecret'), trigger: 'blur' }
        ]
      }
    },
    
    securityConfigRules() {
      return {
        sessionTimeout: [
          { required: true, message: this.$t('system.enterSessionTimeout'), trigger: 'blur' }
        ]
      }
    },
    
    emailConfigRules() {
      return {
        smtpServer: [
          { required: true, message: this.$t('system.enterSmtpServer'), trigger: 'blur' }
        ],
        smtpPort: [
          { required: true, message: this.$t('system.enterSmtpPort'), trigger: 'blur' }
        ],
        senderEmail: [
          { required: true, message: this.$t('system.enterSenderEmail'), trigger: 'blur' },
          { type: 'email', message: this.$t('system.enterValidEmail'), trigger: 'blur' }
        ]
      }
    },
    
    storageConfigRules() {
      return {
        storagePath: [
          { required: true, message: this.$t('system.enterStoragePath'), trigger: 'blur' }
        ],
        maxFileSize: [
          { required: true, message: this.$t('system.enterMaxFileSize'), trigger: 'blur' }
        ]
      }
    }
  },
  
  mounted() {
    this.loadSystemConfig()
  },
  
  methods: {
    hasPermission,
    async loadSystemConfig() {
      this.loading = true
      try {
        const res = await getSystemConfig()
        const config = res.data || res
        if (config) {
          if (config.basic) this.basicConfig = { ...this.basicConfig, ...config.basic }
          if (config.security) this.securityConfig = { ...this.securityConfig, ...config.security }
          if (config.email) this.emailConfig = { ...this.emailConfig, ...config.email }
          if (config.storage) this.storageConfig = { ...this.storageConfig, ...config.storage }
          if (config.server) this.serverConfig = { ...this.serverConfig, ...config.server }
        }
      } catch (error) {
        console.error('Load config error:', error)
        this.$message.error(this.$t('common.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    
    async handleSaveBasicConfig() {
      try {
        await this.$refs.basicConfigForm.validate()
        await updateBasicConfig(this.basicConfig)
        this.$message.success(this.$t('common.saveSuccess'))
      } catch (error) {
        if (error !== false) this.$message.error(this.$t('common.saveFailed'))
      }
    },

    async handleSaveServerConfig() {
      try {
        await this.$refs.serverConfigForm.validate()
        await updateServerConfig(this.serverConfig)
        this.$message.success(this.$t('common.saveSuccess'))
      } catch (error) {
        if (error !== false) this.$message.error(this.$t('common.saveFailed'))
      }
    },

    async handleInitSecret() {
      try {
        await this.$confirm(this.$t('common.tips'), this.$t('system.confirmInitSecret'), {
          type: 'warning'
        })
        await initServerSecret()
        this.$message.success(this.$t('system.initSecretSuccess'))
        // 重新加载服务端配置以获取新生成的密钥
        const res = await getServerConfig()
        const serverData = res.data || res
        if (serverData) {
          this.serverConfig.serverSecret = serverData.serverSecret
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('system.initSecretFailed'))
        }
      }
    },
    
    async handleSaveSecurityConfig() {
      try {
        await this.$refs.securityConfigForm.validate()
        await updateSecurityConfig(this.securityConfig)
        this.$message.success(this.$t('common.saveSuccess'))
      } catch (error) {
        if (error !== false) this.$message.error(this.$t('common.saveFailed'))
      }
    },
    
    async handleSaveEmailConfig() {
      try {
        await this.$refs.emailConfigForm.validate()
        await updateEmailConfig(this.emailConfig)
        this.$message.success(this.$t('common.saveSuccess'))
      } catch (error) {
        if (error !== false) this.$message.error(this.$t('common.saveFailed'))
      }
    },
    
    async handleSaveStorageConfig() {
      try {
        await this.$refs.storageConfigForm.validate()
        await updateStorageConfig(this.storageConfig)
        this.$message.success(this.$t('common.saveSuccess'))
      } catch (error) {
        if (error !== false) this.$message.error(this.$t('common.saveFailed'))
      }
    },
    
    async handleTestEmail() {
      try {
        this.loading = true
        await testEmailConfig(this.emailConfig)
        this.$message.success(this.$t('system.emailTestSuccess'))
      } catch (error) {
        this.$message.error(this.$t('system.emailTestFailed'))
      } finally {
        this.loading = false
      }
    },
    
    async handleCleanupStorage() {
      try {
        await cleanupStorage(this.storageConfig.cleanupDays)
        this.$message.success(this.$t('system.cleanupSuccess'))
      } catch (error) {
        this.$message.error(this.$t('system.cleanupFailed'))
      }
    },
    
    handleResetBasicConfig() {
      this.loadSystemConfig()
    },

    handleResetServerConfig() {
      this.loadSystemConfig()
    },
    
    handleResetSecurityConfig() {
      this.loadSystemConfig()
    },
    
    handleResetEmailConfig() {
      this.loadSystemConfig()
    },
    
    handleResetStorageConfig() {
      this.loadSystemConfig()
    }
  }
}
</script>

<style scoped lang="scss">
.system-config-container {
  padding: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff;
}

/* 统一控制中心样式 */
.control-panel {
  flex-shrink: 0;
  padding: 0 0 10px 0;
  border-bottom: none;
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 14px;
  font-weight: normal;
  
  i {
    font-size: 20px;
    color: #409eff;
  }
}

.config-tabs {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.05);
  }

  :deep(.el-tabs__header) {
    background-color: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
    margin: 0;
    padding: 0 10px;
  }

  :deep(.el-tabs__item) {
    height: 48px;
    line-height: 48px;
    font-size: 13px;
    color: #64748b;
    padding: 0 20px !important;
    
    &.is-active {
      color: #409eff;
      font-weight: 600;
    }
    
    i {
      margin-right: 6px;
    }
  }

  :deep(.el-tabs__nav-wrap::after) {
    display: none;
  }

  :deep(.el-tabs__active-bar) {
    height: 3px;
    border-radius: 3px 3px 0 0;
  }

  :deep(.el-tabs__content) {
    flex: 1;
    overflow-y: auto;
    padding: 0;
    background-color: #fff;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #e2e8f0;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
}

.config-content {
  padding: 40px 30px;
  max-width: 1000px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f1f5f9;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 60px;
    height: 2px;
    background: #409eff;
    border-radius: 2px;
  }

  h3 {
    margin: 0 0 10px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
  }

  .section-desc {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    line-height: 1.6;
  }
}

.config-card {
  margin-bottom: 0;
  border: none;
  box-shadow: none;
  background: transparent;

  :deep(.el-card__body) {
    padding: 0;
  }
}

.form-actions {
  margin-top: 40px;
  padding: 30px 0;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: center;
  gap: 16px;
}

.form-tip {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
  margin-top: 8px;
  padding: 10px 14px;
  background-color: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #e2e8f0;
}

:deep(.el-form) {
  .el-form-item {
    margin-bottom: 24px;
  }

  .el-form-item__label {
    font-weight: 600;
    color: #475569;
    font-size: 13px;
    line-height: 32px;
  }

  .el-input__inner, .el-textarea__inner, .el-input-number {
    border-radius: 8px;
    border-color: #e2e8f0;
    background-color: #f8fafc;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      border-color: #cbd5e1;
    }
    
    &:focus {
      background-color: #fff;
      border-color: #409eff;
      box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
    }
  }

  .el-input-number {
    .el-input-number__decrease, .el-input-number__increase {
      background-color: #f1f5f9;
      border-color: #e2e8f0;
      &:hover {
        color: #409eff;
      }
    }
  }

  .el-button {
    border-radius: 8px;
    font-weight: 600;
    padding: 10px 24px;
    transition: all 0.2s;
    
    &--primary {
      box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .system-config-container {
    padding: 5px;
  }
  
  :deep(.el-form-item__label) {
    float: none;
    display: block;
    text-align: left;
    width: 100% !important;
  }
  
  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}
</style>
